import axios from 'axios';
import {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import LockIcon from '@mui/icons-material/Lock';

import Loading from '../../common/Loading';
import Error from '../../common/Error';
import ProjectsEditManagerList from './ProjectsEditManagerList';
import ProjectsEditUnassignedLoadsLookup from './ProjectsEditUnassignedLoadsLookup';
import ProjectsEditLockLoadsLookup from './ProjectsEditLockLoadsLookup';
import ProjectsEditLoadsList from './ProjectsEditLoadsList';
import ProjectsEditStatus from './ProjectsEditStatus';
import EditRegion from '../../common/EditRegion';

import { useSessionContext } from '../../../contexts/SessionContext';


export default function ProjectsEdit(props) {
    //console.debug("RTS Break ProjectsEdit");

    const theme = useTheme();
    const smallForm = useMediaQuery(theme.breakpoints.down("sm"));

    const {user, sessionCustomer, userAuthToken, apiRoot} = useSessionContext();

    const history = useNavigate();
    const display = props.display;
    const mode = props.mode;

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorBody, setErrorBody] = useState("");
    const [errorTitle, setErrorTitle] = useState("Validation Error");
    const [showLoadsModal, setShowLoadsModal] = useState(false);
    const [showLoadLocksModal, setShowLoadLocksModal] = useState(false);    

    const [idGuid, setIdGuid] = useState("00000000-0000-0000-0000-000000000000");
    const [name, setName] = useState('');
    const [desc, setDesc] = useState('');
    const [manager, setManager] = useState(null);
    const [idExt, setIdExt] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [status, setStatus] = useState(0);
    const [loadList, setLoadList] = useState([]);
    const [address1, setAddress1] = useState('');
    const [address2, setAddress2] = useState('');
    const [city, setCity] = useState('');
    const [region, setRegion] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [hours, setHours] = useState('');
    const [daysAvailable, setDaysAvailable] = useState('');
    const [siteCloseDate, setSiteCloseDate] = useState(null);


    const fetchData = useCallback((pIdGuid) => {
        let apiURL = apiRoot + "/Projects/GetProjectById/" + pIdGuid;
        axios
        .get(
            apiURL,
            {headers:{'Authorization': 'Bearer ' + userAuthToken.token }}
        )
        .then(response => {
            if (response.data) {
                setIdGuid(response.data.idGuid);
                setName(response.data.name);
                setDesc(response.data.desc);
                setManager(response.data.manager);
                setIdExt(response.data.idExt);
                setStartDate(response.data.startDate!=null?new Date(response.data.startDate):null);
                setEndDate(response.data.endDate!=null?new Date(response.data.endDate):null);
                setStatus(response.data.status);
                setLoadList(response.data.loads);
                setAddress1(response.data.address1);
                setAddress2(response.data.address2);
                setCity(response.data.city);
                setRegion(response.data.region);
                setZipCode(response.data.zip);
                setHours(response.data.shipRecHours);
                setDaysAvailable(response.data.daysAvailable);
                setSiteCloseDate(response.data.siteCloseDate!=null?new Date(response.data.siteCloseDate):null);

                setIsLoading(false);
            }
        })
        .catch(error => {
            setHasError(true);
            console.log("RTS Data Error", error);
        });
    },[apiRoot, userAuthToken.token]); 
    
    
    useEffect(() => {
        if(mode === 1){
            setIdGuid(props.idGuid);
            fetchData(props.idGuid);
        }
        else{
            setIsLoading(false);
        }
    }, [mode, fetchData, props.idGuid]);  


    function handleSubmit(e) {
        e.preventDefault();
        setIsLoading(true);

        let apiObj = {
            IdGuid: idGuid,
            Customer: sessionCustomer.customer,
            Name: name,
            Desc: desc,
            Manager: manager,
            IdExt: idExt,
            StartDate: startDate!=null?startDate:null,
            EndDate: endDate!=null?endDate:null,
            Status: status,
            Address1: address1,
            Address2: address2,
            City: city,
            Region: region,
            Zip: zipCode,
            Country: null,    
            ShipRecHours: hours,
            DaysAvailable: daysAvailable,
            SiteCloseDate: siteCloseDate,
			Loads: loadList,
            modifyUser: user.userName
        }

        if(mode > 0){
            let apiURL = apiRoot + "/Projects/UpdateProject/";
            axios
            .put(
                apiURL,
                apiObj, 
                {headers:{'Authorization': 'Bearer ' + userAuthToken.token }}
            )
            .then(response => {
                history(-1);
            })
            .catch(error => {
                setIsLoading(false);
                setErrorTitle("Update Error");
                setErrorBody("An error has occured.  Please review the console log for details.");
                console.log("RTS UpdateProject Error", error);
                setShowErrorModal(true);
            });
        }
        else{
            apiObj.createUser = user.userName;
            let apiURL = apiRoot + "/Projects/CreateProject";
            axios
            .post(
                apiURL,
                apiObj, 
                {headers:{'Authorization': 'Bearer ' + userAuthToken.token }}
            )
            .then(response => {
                history(-1);
            })
            .catch(error => {
                setErrorTitle("Create Error");
                setErrorBody("An error has occured.  Please review the console log for details.");
                console.log("RTS CreateProject Error", error);
                setShowErrorModal(true);
            });
        }
    } 


    function addLoads(args){
        setShowLoadsModal(false);
        let curLoadList = JSON.parse(JSON.stringify(loadList));

        args.forEach((item) => {
            if(curLoadList.filter(l => l.idGuid === item.idGuid).length > 0){
                return;
            }

            //let curLoadList = loadList.filter((l) => l.idGuid !== item.idGuid);
            curLoadList.push(item);
        });
            
        setLoadList(curLoadList);
        props.setFormDirtyFx(1);
    }

    
    function removeLoad(args){
        let curLoadList = loadList.filter((l) => l.idGuid !== args.idGuid);
        setLoadList(curLoadList);
        props.setFormDirtyFx(1);
    };


    function openLoadsModal(){
        setShowLoadsModal(true);
    };


    function openLockReelsModal(){
        setShowLoadLocksModal(true);
    };


    function addLoadLocks(args){
        let apiObj = [];
        args.forEach((item, idx) => {
            apiObj[idx] = {AppUser:user, Customer:sessionCustomer.customer, Load:item, Action:1};
        });

        if(apiObj.length <= 0){
            setShowLoadLocksModal(false);
            return;
        }

        let apiURL = apiRoot + "/LoadLocks/AddLoadLocks";
        axios
        .post(
            apiURL,
            apiObj, 
            {headers:{'Authorization': 'Bearer ' + userAuthToken.token }}
        )
        .then(response => {
            setShowLoadLocksModal(false);
            setIsLoading(false);

            setErrorTitle("Reels Locked");
            setErrorBody("All selected reels are locked.");
            setShowErrorModal(true);

        })
        .catch(error => {
            setIsLoading(false);
            setErrorTitle("Update Error");
            setErrorBody("An error has occured.  Please review the console log for details.");
            console.log("RTS UpdateProject Error", error);
            setShowErrorModal(true);
        });        

    }

    if(hasError){
        return(
            <Error />
        );
    };
    
    
    if(isLoading){
        return(
            <Loading />
        );
    };   


    return (
        <>
        <Dialog open={showErrorModal} onClose={()=>{setShowErrorModal(false);}}>
            <DialogTitle>{errorTitle}</DialogTitle>
            <DialogContent>
                <DialogContentText>{errorBody}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={e => {setShowErrorModal(false);}}>Close</Button>
            </DialogActions>
        </Dialog>  
        <Dialog fullWidth={true} maxWidth="md" open={showLoadsModal}>
            <DialogTitle>Add Reel</DialogTitle>
            <DialogContent>
                <Box sx={{minHeight:"400px"}}>
                    <ProjectsEditUnassignedLoadsLookup display={display} addLoadsFx={addLoads} setShowLoadsModalFx={setShowLoadsModal} />
                </Box>            
            </DialogContent>
        </Dialog>
        <Dialog fullWidth={true} maxWidth="md" open={showLoadLocksModal}>
            <DialogTitle>Lock Reel</DialogTitle>
            <DialogContent>
                <Box sx={{minHeight:"400px"}}>
                    <ProjectsEditLockLoadsLookup display={display} loadList={loadList} addLoadLocksFx={addLoadLocks} setShowLoadLocksModalFx={setShowLoadLocksModal} />
                </Box>            
            </DialogContent>
        </Dialog>           
        <Box>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
            <form id="projectForm" onSubmit={handleSubmit}>
                <Box maxWidth="md" sx={{display:'flex', alignItems:'center', justifyContent:'center'}}>
                <Box id="smBox" maxWidth="sm" sx={{width:smallForm?'auto':'600px'}}>
                <TextField
                    id="tbName"
                    margin="normal"
                    fullWidth
                    label="Project Name"
                    size="small"
                    required
                    value={name}
                    onChange={(e) => {props.setFormDirtyFx(1); setName(e.target.value);}}
                />
                <TextField
                    id="tbDescription"
                    margin="normal"
                    fullWidth
                    label="Description"
                    size="small"
                    value={desc}
                    multiline
                    rows={3}
                    onChange={(e) => {props.setFormDirtyFx(1); setDesc(e.target.value);}}
                />
                <ProjectsEditManagerList manager={manager} setManagerFx={setManager} setFormDirtyFx={props.setFormDirtyFx} />
                <TextField
                    id="tbIdExt"
                    margin="normal"
                    fullWidth
                    label="External Id"
                    size="small"
                    value={idExt}
                    onChange={(e) => {props.setFormDirtyFx(1); setIdExt(e.target.value);}}
                />
                <Grid container spacing={1} sx={{mt:1, mb:1}}>
                    <Grid item xs={6}>
                        <DatePicker
                            id="tbStartDate"
                            label="Start Date"
                            inputFormat="MM/dd/yyyy"
                            value={startDate}
                            onChange={(val) => {props.setFormDirtyFx(1); setStartDate(val)}}
                            slotProps={{ field: { clearable: true }, textField:{size:'small'} }}
                        />      
                    </Grid>
                    <Grid item xs={6} sx={{textAlign:'right'}}>
                        <DatePicker
                            id="tbEndDate"
                            label="End Date"
                            inputFormat="MM/dd/yyyy"
                            value={endDate}
                            onChange={(val) => {props.setFormDirtyFx(1); setEndDate(val)}}
                            slotProps={{ field: { clearable: true }, textField:{size:'small'} }}
                        />  
                    </Grid>
                </Grid>
                <ProjectsEditStatus status={status} setStatusFx={setStatus} setFormDirtyFx={props.setFormDirtyFx} />
                <Typography variant="h6" sx={{mt:2, mb:1}}>Pickup Requests Information</Typography>
                <TextField
                    id="tbAddress1"
                    margin="none"
                    fullWidth
                    label="Street Address"
                    size="small"
                    value={address1}
                    onChange={(e) => {props.setFormDirtyFx(1); setAddress1(e.target.value);}}
                />
                <TextField
                    id="tbAddress2"
                    margin="dense"
                    fullWidth
                    size="small"
                    value={address2}
                    onChange={(e) => {props.setFormDirtyFx(1); setAddress2(e.target.value);}}
                />
                <TextField
                    id="tbCity"
                    margin="normal"
                    fullWidth
                    label="City"
                    size="small"
                    value={city}
                    onChange={(e) => {props.setFormDirtyFx(1); setCity(e.target.value);}}
                />
                <Grid container>
                    <Grid item xs={6} sx={{pr:1}}>
                        <EditRegion disable={0} region={region} setRegionFx={setRegion} />
                    </Grid>
                    <Grid item xs={6} sx={{pl:1}}>
                        <TextField
                            id="tbZip"
                            margin="normal"
                            fullWidth
                            label="Zip Code"
                            size="small"
                            value={zipCode}
                            onChange={(e) => {props.setFormDirtyFx(1); setZipCode(e.target.value);}}
                        />
                    </Grid>
                </Grid>
                <TextField
                    id="tbHours"
                    margin="normal"
                    fullWidth
                    label="Shipping/Receiving Hours"
                    size="small"
                    value={hours}
                    onChange={(e) => {props.setFormDirtyFx(1); setHours(e.target.value);}}
                />
                <TextField
                    id="tbDaysAvailable"
                    margin="normal"
                    fullWidth
                    label="Days Available"
                    size="small"
                    value={daysAvailable}
                    onChange={(e) => {props.setFormDirtyFx(1); setDaysAvailable(e.target.value);}}
                />
                <DatePicker
                    id="tbSiteCloseDate"
                    label="Site Close Date"
                    inputFormat="MM/dd/yyyy"
                    value={siteCloseDate}
                    onChange={(val) => {props.setFormDirtyFx(1); setSiteCloseDate(val)}}
                    slotProps={{ field: { clearable: true }, textField:{size:'small'} }}
                    sx={{mt:2}}
                /> 
                </Box>
                </Box>
                <Box sx={{mt:2}}>
                    <Grid container sx={{mb:1}}>
                        <Grid item xs={6}>
                            <Typography variant='h6'>Project Reels</Typography>
                        </Grid>
                        <Grid item xs={6} sx={{textAlign:'right'}}>
                            <Button variant='contained' onClick={openLoadsModal}>Add Reels</Button>
                            <Button variant='contained' startIcon={<LockIcon />} onClick={openLockReelsModal} sx={{ml:2}}>Lock Reels</Button>
                        </Grid>
                    </Grid>
                    <ProjectsEditLoadsList data={loadList} removeLoadFx={removeLoad} />
                </Box>
                <Box sx={{textAlign:'center', mt:3}}>
                    <Button variant="contained" disabled={props.formDirty===1?false:true} type="submit" sx={{mr:2}}>Save</Button>
               </Box>
            </form>
            </LocalizationProvider>
        </Box>
        </>
    );

}
